import React, { useState, useRef, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { ADAARIARoles } from '../../../../models/ADA';
import { keepSpaceTogether, getNavbarHeight, getViewportDimensions } from '../../../../models/Utils';

import './TwoColumnSlider.scss';

const togetherString = 'V Team';
const mobileScreenThreshold = 767; // MUST BE IN SYNC WITH CSS

const TwoColumnSlider = React.forwardRef((props, theRef) => {
  const mobileMinHeight = 40 + 2 * 32 + 40; // padding-top + 2 * title_height + padding-bottom from CSS
  const [ mobileMaxHeight, setMobileMaxHeight ] = useState(0);
  const refMobileMaxHeight = useRef(mobileMaxHeight); // 40vh from CSS
  const mobileSpaceBetween = 10; // margin-top from CSS
  const [ isMobileLayout, setIsMobileLayout ] = useState(false);
  const refIsMobileLayout = useRef(isMobileLayout);
  const menuOpened = useSelector(state => state.data.menuOpened);
  const container = useRef(null);
  const [activeColumn, setActiveColumn] = useState(-1);
  const columns = props.data.Boxes.filter((box, i) => i < 2).map(box => ({
    title: box.Header,
    text: box.Body,
    image: box.Background.Image,
    bgndOpacity: box.Background.Opacity,
    bgndOverlayColor: box.Background.Overlay
  }));
  const columnRefs = columns.map((column) => useRef(null));
  const columnBodyTextRefs = columns.map((column) => useRef(null));

  const getTwoColumns = () => {
    let items = [];
    columns.filter((column, i) => i < 2).forEach((column, i) => {
      items.push(
        <div className="cpvi-grid-col w6" key={i}>
          <div 
            className={`bbf-2cs-column ${activeColumn === -1 ? '' : (activeColumn === i ? 'active' : '')}`}
            ref={columnRefs[i]}
            onMouseEnter={() => handleColumnMouseEnter(i)}
            onMouseLeave={() => handleColumnMouseLeave()}
            onFocus={() => handleColumnMouseEnter(i)}
            onBlur={() => handleColumnMouseLeave()}
            //tabIndex={!menuOpened ? 0 : -1}
          >
            <div className="bbf-2cs-column--bgnd-wrapper">
              <div
                className="bbf-2cs-column--bgnd"
                style={{
                  backgroundImage: `url("${column.image}")`,
                  height: isMobileLayout ? `${mobileMaxHeight}px` : ''
                }}
                role="img"
                alt={column.image.AltText}
              >
                <div className="bbf-2cs-column--bgnd-overlay" style={{ opacity: column.bgndOpacity || 0, backgroundColor: column.bgndOverlayColor || null }}></div>
              </div>
            </div>
            <div className="bbf-2cs-column--text-wrapper">
              <div className="bbf-2cs-column--title" role={ADAARIARoles.Heading}>{keepSpaceTogether(column.title, togetherString)}</div>
              <div className="ada-hidden">:</div>
              <div className="bbf-2cs-column--text" ref={columnBodyTextRefs[i]}>
                <div className="bbf-2cs-column--text-inner">{keepSpaceTogether(column.text, togetherString)}</div>
              </div>
            </div>
          </div>
        </div>
      );
    });
    return items;
  };

  const handleColumnMouseEnter = (column) => {
    setActiveColumn(column);
    const columnElement = columnBodyTextRefs[column].current;
    const innerTextHeight = columnElement.getElementsByClassName('bbf-2cs-column--text-inner')[0].getBoundingClientRect().height;
    columnElement.style.height = `${innerTextHeight}px`;
  };

  const handleColumnMouseLeave = () => {
    setActiveColumn(-1);
    columnBodyTextRefs.forEach((ref) => ref.current.style.height = '0px');
  };

  const handleScroll = () => {
    if (!refIsMobileLayout.current) return;

    const viewport = getViewportDimensions();
    columnRefs.forEach((column, i) => {
      const rect = column.current.getBoundingClientRect();
      const bodyText = columnBodyTextRefs[i].current;
      const innerTextHeight = bodyText.getElementsByClassName('bbf-2cs-column--text-inner')[0].getBoundingClientRect().height;
      bodyText.style.height = rect.y + rect.height + mobileSpaceBetween <= viewport.height ? `${innerTextHeight}px` : '0px';
    });
  };

  const handleResize = () => {
    //theRef.current.style.marginBottom = `${getNavbarHeight()}px`;
    const viewport = getViewportDimensions();
    const onMobile = viewport.width <= mobileScreenThreshold;
    setIsMobileLayout(onMobile);
    refIsMobileLayout.current = onMobile;
    if (!onMobile) {
      columnRefs.forEach((columnRef, i) => {
        columnRef.current.style.height = '';
        columnBodyTextRefs[i].current.style.opacity = '';
      });
    } else {
      const maxH = getViewportDimensions().height * 0.4;
      setMobileMaxHeight(maxH);
      refMobileMaxHeight.current = maxH;
    }
  };

  useLayoutEffect(() => {
    handleResize();
    handleScroll();

    window.addEventListener('resize', handleResize);
    document.body.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="bbf-2-column-slider" ref={theRef}>
      <div className="bbf-2cs-wrapper cpvi-grid-container" ref={container}>
        <div className="bbf-2cs-intro-wrapper cpvi-grid-row">
          <div className="bbf-2cs-intro-text cpvi-grid-col w6">
            <div className="bbf-2cs-title" role={ADAARIARoles.Heading} >{props.data.Header}</div>
          </div>
          <div className="bbf-2cs-intro-text cpvi-grid-col w6">
            <div className="bbf-2cs-text">
              <div className="cont-75 bbf-2cs-text-inner">{props.data.Body}</div>
            </div>
          </div>
        </div>
        <div className="cpvi-grid-row">
          {getTwoColumns()}
        </div>
      </div>
    </div>
  );
});

export default TwoColumnSlider;
